import { Text } from '@doordash/design-language'
import * as React from 'react'
import { connect } from 'react-redux'
import * as events from '../../../events'
import { useDesktopBreakpoint } from '../../../hooks/useDesktopBreakpoint'
import * as DA from '../../../typeModules/events'
import Cell from './cell'
import Item from './item'
import {
  InlineContent,
  SectionContainer,
  SectionWrapper,
  StackContent,
  TextContainer,
} from './styles'

export const enum Direction {
  Column = 'Column',
  Row = 'Row',
}

export const enum TextType {
  Text = 'Text',
  List = 'List',
}

type TextItem = {
  text: string[]
  subtitle?: string
  trackingId?: string
}

type TextEntry = {
  content: TextItem[]
  subtitle?: string
}

type TextSectionProps = {
  title: string
  text: TextEntry
  center?: boolean
  direction?: Direction
  type?: TextType
  style?: React.CSSProperties
  disableDesktopWidth?: boolean
  initialState?: number[] // Initial state is an ordered array of indices
}

type ReduxProps = {
  trackLandingPageSignUpDetails?: (
    data: DA.LandingPageSignUpDetails
  ) => Promise<any>
}

const ContentBody = (
  type: TextType,
  text: TextEntry,
  doToggle: (idx: number) => void,
  state,
  isDesktopWidth: boolean
) => {
  return type === TextType.List
    ? text.content.map((textItem: TextItem, idx: number) => (
        <Cell
          key={`${textItem.text.slice(-10)}${idx}`}
          isToggled={isDesktopWidth ? true : state[idx]}
          isToggleable={!isDesktopWidth}
          onClick={isDesktopWidth ? null : () => doToggle(idx)}
          title={textItem.subtitle}
          bodytext={textItem.text}
          idx={idx}
          isDesktopWidth={isDesktopWidth}
        />
      ))
    : text.content.map((textItem: TextItem, idx: number) => (
        <Item key={`item${idx}`} textItem={textItem} idx={idx} />
      ))
}

// Takes in an ordered array of indices and generates an array of
// booleans that will be used to set up the initial state that
// text-section will use to determine the isToggled state for
// each cell.
const generateState = (initialState) => {
  const length = initialState.length
  if (length > 0) {
    // Generates an array up to the last index included and
    // fills them with either true or false
    const lastTrueState = initialState[length-1]
    const generatedArray = new Array(lastTrueState+1).fill(false)
    for (let i=0; i<length; i++) {
      generatedArray[initialState[i]] = true
    }
    return generatedArray
  }
  // If initial state is empty array, passes that back
  return initialState
}

export const TextSection: React.FC<TextSectionProps & ReduxProps> = ({
  title,
  text,
  center = false,
  direction = Direction.Column,
  type = TextType.Text,
  style = {},
  disableDesktopWidth = false,
  initialState = [],
  trackLandingPageSignUpDetails,
}) => {

  const [state, toggle] = React.useState(generateState(initialState))
  const isDesktopWidth = useDesktopBreakpoint() && !disableDesktopWidth

  const doToggle = (idx: number) => {
    toggle(prevState => {
      const prev = [...prevState]
      prev[idx] = !prev[idx]

      trackLandingPageSignUpDetails({
        action: DA.EventActions.Clicked,
        name: prev[idx]
          ? DA.SignUpDetailsNames.Opened
          : DA.SignUpDetailsNames.Closed,
        title: text.content[idx].trackingId,
      })

      return prev
    })
  }

  const sectionStyle: React.CSSProperties = {
    flexDirection: (direction === Direction.Row) ? 'row' : 'column',
    minWidth: '100%',
  }

  return (
    <SectionWrapper>
      <SectionContainer style={style} center={center}>
        <Text styles={Text.Styles.Bold32}>{title}</Text>
        {text.subtitle && (
          <Text styles={Text.Styles.Bold18}>{text.subtitle}</Text>
        )}
        <TextContainer style={sectionStyle}>
          {isDesktopWidth && (
            <InlineContent>
              {ContentBody(type, text, doToggle, state, isDesktopWidth)}
            </InlineContent>
          )}
          {!isDesktopWidth && (
            <StackContent>
              {ContentBody(type, text, doToggle, state, isDesktopWidth)}
            </StackContent>
          )}
        </TextContainer>
      </SectionContainer>
    </SectionWrapper>
  )
}

const mapDispatchToProps = {
  trackLandingPageSignUpDetails: events.trackLandingPageSignUpDetails,
}

export default connect(null, mapDispatchToProps)(TextSection)
